import * as React from "react";
import { useLocation } from "wouter";
import { Search, X, MapPin, Package, ArrowRight } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { cn } from "@/lib/utils.js";

interface SearchItem {
  id: string; name: string; spaceName: string | null; quantity: number; photoUrl: string | null;
  tags: string[];
}
interface SearchSpace { id: string; name: string; type: string; }
interface SearchResults { items: SearchItem[]; spaces: SearchSpace[]; total: number; }

export function SearchModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<SearchResults | null>(null);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [, navigate] = useLocation();

  React.useEffect(() => {
    if (open) { setQuery(""); setResults(null); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  React.useEffect(() => {
    if (!query.trim()) { setResults(null); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        setResults(await apiFetch<SearchResults>(`/api/search?q=${encodeURIComponent(query)}`));
      } catch {} finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); onClose(); }
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" onClick={onClose}>
      <div className="fixed inset-0 bg-ink/30" />
      <div className="relative w-full max-w-xl bg-card rounded-lg shadow-xl border border-clay-border overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-clay-border">
          <Search className="w-5 h-5 text-faint shrink-0" />
          <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={'Try "where is the screwdriver" or "what\'s in the garage"'}
            className="flex-1 text-sm text-body outline-none bg-transparent placeholder:text-faint" />
          <kbd className="hidden sm:inline text-[0.8125rem] text-faint border border-clay-border rounded-sm px-1.5 py-0.5">ESC</kbd>
          <button onClick={onClose} aria-label="Close search" className="p-1 rounded-md text-faint hover:bg-amethyst-soft hover:text-amethyst-ink transition-colors"><X className="w-4 h-4" /></button>
        </div>

        {loading && <div className="px-4 py-6 text-center text-sm text-faint">Searching…</div>}

        {results && !loading && results.total === 0 && (
          <div className="px-4 py-8 text-center text-sm text-faint">No results found</div>
        )}

        {results && !loading && results.total > 0 && (
          <div className="max-h-[50vh] overflow-y-auto">
            {results.spaces.length > 0 && (
              <div className="px-4 pt-3 pb-1">
                <p className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wider mb-2">Spaces</p>
                {results.spaces.map((space) => (
                  <button key={space.id} onClick={() => { navigate("/spaces"); onClose(); }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-amethyst-soft text-left transition-colors">
                    <MapPin className="w-4 h-4 text-sage shrink-0" />
                    <span className="text-sm font-medium text-body">{space.name}</span>
                    <span className="text-[0.8125rem] text-faint ml-auto">{space.type}</span>
                  </button>
                ))}
              </div>
            )}
            {results.items.length > 0 && (
              <div className="px-4 pt-3 pb-3">
                <p className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wider mb-2">Items</p>
                {results.items.map((item) => (
                  <button key={item.id} onClick={() => { navigate(`/inventory?highlight=${item.id}`); onClose(); }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-amethyst-soft text-left transition-colors">
                    {item.photoUrl ? (
                      <img src={item.photoUrl} alt="" loading="lazy" className="w-8 h-8 rounded-md object-cover shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0">
                        <Package className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-body truncate">{item.name}</p>
                      <p className="text-[0.8125rem] text-faint truncate">
                        {item.spaceName && <><MapPin className="w-3 h-3 inline mr-1" />{item.spaceName}</>}
                        {item.quantity > 1 && <span className="ml-2 tabular-nums">×{item.quantity}</span>}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-faint shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!query && (
          <div className="px-4 py-6 text-center text-[0.8125rem] text-faint">
            Search items, spaces, tags, and notes
          </div>
        )}
      </div>
    </div>
  );
}

export function SearchTrigger({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 rounded-md bg-paper-raised border border-clay-border hover:border-primary/40 text-faint text-sm transition-colors w-full max-w-xs">
      <Search className="w-4 h-4" />
      <span className="flex-1 text-left">Search…</span>
      <kbd className="hidden sm:inline text-[0.8125rem] border border-clay-border rounded-sm px-1.5 py-0.5">⌘K</kbd>
    </button>
  );
}
